<template>
  <div>
    <app-template>
      <div class="min-h-screen flex">

        <!-- SIDEBAR -->
        <aside class="shrink-0">
          <nav class="px-3 space-y-0.5">
            <button
              v-for="item in navItems" :key="item.id"
              class="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all"
              :class="activeTab === item.id
                ? 'bg-orange/10 text-slate-900'
                : 'text-slate-900/40 hover:text-slate-900/60 hover:bg-slate-900/5'"
              @click="switchTab(item.id)"
            >
              <i
                :class="[item.icon, 'text-xs w-4 text-center shrink-0', activeTab === item.id ? 'text-orange-400' : '']"
              ></i>
              <span class="text-sm">{{ item.label }}</span>
              <span
                v-if="item.id === 'factures' && factures.length"
                class="ml-auto text-[10px] font-mono bg-white/10 text-white/30 px-1.5 py-0.5 rounded-full"
              >{{ factures.length }}</span>
            </button>
          </nav>
        </aside>


        <!-- ═══════════════════════════════════════
             CONTENU
        ═══════════════════════════════════════ -->
        <main class="flex-1 min-w-0">

          <div v-if="loading" class="flex flex-col items-center justify-center min-h-screen gap-3 text-neutral-400">
            <div class="w-7 h-7 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div>
            <p class="text-sm">Chargement…</p>
          </div>

          <div v-else class="px-8">

            <!-- ══════════════════════════════
                 NON ABONNÉ — tunnel
            ══════════════════════════════ -->
            <template v-if="!isSubscribed">

              <div v-if="societe.config_warnings && societe.config_warnings.settings_abonnement == 0" class="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800">
                <i class="fas fa-hourglass-half mt-0.5 shrink-0 text-amber-500"></i>
                <div>
                  <p class="font-semibold">Période d'essai en cours</p>
                  <p class="text-amber-600 mt-0.5 text-xs">Choisissez votre formule pour continuer sans interruption.</p>
                </div>
              </div>

              <!-- Configurer -->
              <section v-if="step === 'configure'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Choisissez votre formule</h2>
                  <p class="text-sm text-neutral-400 mt-1">Sans engagement · Résiliable à tout moment</p>
                </div>
                <div class="space-y-5">
                  <template v-if="!isNegotiatedRate">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <button v-for="plan in plans" :key="plan.id" type="button"
                        class="text-left border-2 rounded-2xl p-6 cursor-pointer transition-all relative focus:outline-none bg-white"
                        :class="selectedPlanId === plan.id ? 'border-orange-500 shadow-[0_0_0_3px_rgba(240,90,0,0.10)]' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm'"
                        @click="selectedPlanId = plan.id"
                      >
                        <div v-if="plan.recommended" class="absolute -top-3 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-[10px] font-mono uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">⭐ Recommandé</div>
                        <div class="flex items-start justify-between mb-5">
                          <div class="w-9 h-9 rounded-lg flex items-center justify-center text-sm" :class="plan.recommended ? 'bg-neutral-900 text-orange-400' : 'bg-orange-50 text-orange-500'"><i :class="plan.icon"></i></div>
                          <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all" :class="selectedPlanId === plan.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                            <i v-if="selectedPlanId === plan.id" class="fas fa-check text-white" style="font-size:8px;"></i>
                          </div>
                        </div>
                        <p class="font-bold text-neutral-900 text-base">{{ plan.name }}</p>
                        <p class="text-xs text-neutral-400 mt-1 mb-5 leading-relaxed">{{ plan.tagline }}</p>
                        <div class="flex items-baseline gap-1 mb-5">
                          <span class="text-3xl font-bold text-neutral-900 tabular-nums">{{ plan.prix }}</span>
                          <span class="text-xs text-neutral-400">€ TTC / licence / mois</span>
                        </div>
                        <ul class="space-y-1.5">
                          <li v-for="f in plan.features" :key="f.label" class="flex items-center gap-2 text-xs" :class="f.included ? 'text-neutral-600' : 'text-neutral-300'">
                            <i class="w-3.5 shrink-0 text-[9px]" :class="f.included ? 'fas fa-check text-orange-500' : 'fas fa-minus text-neutral-300'"></i>{{ f.label }}
                          </li>
                        </ul>
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="bg-white border border-neutral-200 rounded-2xl p-6">
                      <div class="flex items-center gap-3 mb-4">
                        <div class="w-9 h-9 rounded-lg bg-neutral-900 flex items-center justify-center"><i class="fas fa-handshake text-orange-400 text-sm"></i></div>
                        <div><p class="font-bold text-neutral-900">Tarif personnalisé</p><p class="text-xs text-neutral-400">Contrat avec tarif négocié spécifique.</p></div>
                      </div>
                      <div class="flex items-baseline gap-1.5 px-5 py-4 bg-neutral-50 border border-neutral-100 rounded-xl">
                        <span class="text-4xl font-bold text-neutral-900 font-mono">{{ fmt(prixEffectif, 'EUR') }}</span>
                        <span class="text-sm text-neutral-400">TTC / licence / mois</span>
                      </div>
                    </div>
                  </template>

                  <div class="bg-white border border-neutral-200 rounded-2xl p-6">
                    <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5">Nombre de licences</p>
                    <div class="flex items-center gap-6 mb-5">
                      <button class="w-12 h-12 rounded-xl border-2 border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-600 flex items-center justify-center text-2xl font-light transition-all disabled:opacity-25 disabled:cursor-not-allowed" :disabled="nbLicences <= 1" @click="nbLicences--">−</button>
                      <div class="flex-1 text-center">
                        <p class="text-6xl font-bold text-neutral-900 tabular-nums leading-none">{{ nbLicences }}</p>
                        <p class="text-xs text-neutral-400 mt-2 font-mono">{{ nbLicences > 1 ? 'licences' : 'licence' }}</p>
                      </div>
                      <button class="w-12 h-12 rounded-xl border-2 border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-600 flex items-center justify-center text-2xl font-light transition-all" @click="nbLicences++">+</button>
                    </div>
                    <input type="range" v-model.number="nbLicences" min="1" max="50" class="w-full h-1.5 rounded-full appearance-none bg-neutral-200 accent-orange-500 cursor-pointer mb-3" />
                    <div class="flex flex-wrap gap-2">
                      <button v-for="q in [1,2,3,5,10,15,20]" :key="q" class="text-xs font-mono px-3 py-1.5 rounded-lg border transition-all" :class="nbLicences === q ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'" @click="nbLicences = q">{{ q }}</button>
                    </div>
                  </div>

                  <div v-if="selectedPlanId || isNegotiatedRate" class="flex items-center justify-between bg-white border border-neutral-200 rounded-2xl px-6 py-5">
                    <div>
                      <p class="text-xs text-neutral-400 font-mono mb-0.5">Total mensuel TTC</p>
                      <p class="text-2xl font-bold text-neutral-900 font-mono">{{ fmt(totalTTC, 'EUR') }}</p>
                    </div>
                    <button class="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all shadow-[0_4px_20px_rgba(240,90,0,0.35)]" @click="goToPayment">
                      Continuer <i class="fas fa-arrow-right text-xs"></i>
                    </button>
                  </div>
                </div>
              </section>

              <!-- Paiement tunnel -->
              <section v-if="step === 'payment'">
                <button class="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-700 transition-colors mb-6" @click="step = 'configure'">
                  <i class="fas fa-arrow-left text-xs"></i> Modifier
                </button>
                <div class="mb-6 flex items-center justify-between bg-white border border-neutral-200 rounded-2xl px-6 py-4">
                  <div>
                    <p class="text-sm font-semibold text-neutral-900">{{ isNegotiatedRate ? 'Tarif personnalisé' : currentPlan?.name }} · {{ nbLicences }} licence{{ nbLicences > 1 ? 's' : '' }}</p>
                    <p class="text-xs text-neutral-400 font-mono">{{ fmt(prixEffectif, 'EUR') }} TTC / licence / mois</p>
                  </div>
                  <p class="text-lg font-bold text-neutral-900 font-mono">{{ fmt(totalTTC, 'EUR') }} / mois</p>
                </div>
                <div class="space-y-4">
                  <div v-if="paymentMethods.length" class="bg-white border border-neutral-200 rounded-2xl p-6">
                    <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-4">Carte bancaire</p>
                    <div class="space-y-2">
                      <label v-for="pm in paymentMethods" :key="pm.id" class="flex items-center justify-between border-2 rounded-xl px-4 py-3 cursor-pointer transition-all" :class="selectedPmId === pm.id ? 'border-orange-500 bg-orange-50/60' : 'border-neutral-200 hover:border-neutral-300'">
                        <input type="radio" :value="pm.id" v-model="selectedPmId" class="absolute opacity-0 w-0 h-0" />
                        <div class="flex items-center gap-3">
                          <i :class="brandIcon(pm.brand)" class="text-2xl text-neutral-400"></i>
                          <div><p class="text-sm font-mono font-semibold text-neutral-800">•••• {{ pm.last4 }}</p><p class="text-xs text-neutral-400">Expire {{ pm.exp_month }}/{{ pm.exp_year }}</p></div>
                        </div>
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all" :class="selectedPmId === pm.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                          <div v-if="selectedPmId === pm.id" class="w-1.5 h-1.5 rounded-full bg-white"></div>
                        </div>
                      </label>
                    </div>
                    <button class="mt-3 w-full border-2 border-dashed border-neutral-200 hover:border-orange-400 hover:bg-orange-50 text-orange-500 text-sm py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all" @click="showNewCard = !showNewCard">
                      <i :class="showNewCard ? 'fas fa-minus' : 'fas fa-plus'" class="text-xs"></i> {{ showNewCard ? 'Annuler' : 'Utiliser une autre carte' }}
                    </button>
                  </div>
                  <div v-show="!paymentMethods.length || showNewCard" class="bg-white border border-neutral-200 rounded-2xl p-6">
                    <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5">{{ paymentMethods.length ? 'Nouvelle carte' : 'Carte bancaire' }}</p>
                    <div class="space-y-4">
                      <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Numéro de carte</label><div ref="cardNumber" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                      <div class="grid grid-cols-2 gap-3">
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Expiration</label><div ref="cardExpiry" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">CVC</label><div ref="cardCvc" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                      </div>
                      <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Titulaire</label><input v-model="cardHolder" type="text" class="w-full border border-neutral-200 rounded-xl px-4 py-3 bg-neutral-50 text-sm text-neutral-900 placeholder-neutral-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all" placeholder="Jean Dupont" /></div>
                    </div>
                    <div class="mt-3 flex flex-wrap items-center gap-3 px-3 py-2 bg-neutral-50 border border-neutral-100 rounded-lg text-xs text-neutral-400 font-mono">
                      <span class="flex items-center gap-1.5"><i class="fas fa-lock text-green-500"></i> SSL 256 bits</span>
                      <span class="text-neutral-300">·</span><span class="flex items-center gap-1.5"><i class="fab fa-stripe" style="color:#635bff;"></i> Stripe</span>
                      <span class="text-neutral-300">·</span><span class="flex items-center gap-1.5"><i class="fas fa-id-card text-blue-400"></i> 3D Secure</span>
                    </div>
                  </div>
                  <div v-if="errorMsg" class="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 text-sm text-red-700"><i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-xs"></i><span>{{ errorMsg }}</span></div>
                  <button class="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-4 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.18)] transition-all disabled:opacity-50 disabled:cursor-not-allowed" :disabled="paying || !canPay" @click="pay">
                    <span v-if="paying"><i class="fas fa-circle-notch fa-spin text-xs"></i>&nbsp; Traitement…</span>
                    <span v-else><i class="fas fa-lock text-xs"></i>&nbsp; Payer {{ fmt(totalTTC, 'EUR') }} / mois</span>
                  </button>
                  <p class="text-center text-xs text-neutral-400">En confirmant, vous acceptez nos <a href="https://storage.victoranw.fr/file/x3jvJaPzGU" target="_blank" class="text-orange-500 hover:underline">CGU</a> et <a href="https://storage.victoranw.fr/file/ADo4JPI8RA" target="_blank" class="text-orange-500 hover:underline">CGV</a>. Renouvellement automatique · Résiliable à tout moment.</p>
                </div>
              </section>

              <!-- Succès tunnel -->
              <section v-if="step === 'success'" class="flex items-center justify-center min-h-[500px]">
                <div class="bg-white border border-neutral-200 rounded-2xl p-12 max-w-md w-full text-center shadow-xl">
                  <div class="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center text-white text-2xl mx-auto mb-6 shadow-[0_8px_24px_rgba(240,90,0,0.35)]"><i class="fas fa-check"></i></div>
                  <h2 class="text-2xl font-bold text-neutral-900 mb-3">Abonnement activé !</h2>
                  <p class="text-sm text-neutral-500 leading-relaxed mb-2">Formule <strong class="text-neutral-700">{{ isNegotiatedRate ? 'personnalisée' : currentPlan?.name }}</strong> · <strong class="text-neutral-700">{{ nbLicences }} licence{{ nbLicences > 1 ? 's' : '' }}</strong></p>
                  <p class="text-sm text-neutral-400 mb-8">Facture envoyée à <strong class="text-neutral-700">{{ societe.mail }}</strong>.</p>
                  <button class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-xl transition-all" @click="handleSuccess">Accéder à mon espace <i class="fas fa-arrow-right text-xs"></i></button>
                </div>
              </section>
            </template>


            <!-- ══════════════════════════════
                 ABONNÉ
            ══════════════════════════════ -->
            <template v-else>

              <!-- SMS -->
              <div v-if="activeTab === 'sms'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Crédits SMS</h2>
                  <p class="text-sm text-neutral-400 mt-1">Rechargez votre solde · Les crédits n'expirent jamais</p>
                </div>

                <div class="bg-neutral-900 text-white rounded-2xl px-7 py-5 mb-6 flex items-center justify-between flex-wrap gap-4">
                  <div class="flex items-center gap-4">
                    <div class="w-11 h-11 rounded-xl bg-orange-500/20 flex items-center justify-center"><i class="fas fa-comment-dots text-orange-400 text-lg"></i></div>
                    <div>
                      <p class="text-xs font-mono uppercase tracking-widest text-white/30 mb-0.5">Solde actuel</p>
                      <p class="text-3xl font-bold tabular-nums">{{ (societe.sms_credits || 0).toLocaleString('fr-FR') }} <span class="text-lg font-normal text-white/40">SMS</span></p>
                    </div>
                  </div>
                  <span v-if="(societe.sms_credits || 0) > 0" class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-green-500/15 text-green-400 border border-green-500/20"><span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span> Crédit actif</span>
                  <span v-else class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-red-500/15 text-red-400 border border-red-500/20"><i class="fas fa-exclamation-triangle" style="font-size:10px;"></i> Solde épuisé</span>
                </div>

                <section v-if="smsStep === 'packs'">
                  <div class="bg-orange-50 border border-orange-100 rounded-xl px-5 py-3.5 mb-6 flex items-start gap-2">
                    <i class="fas fa-lightbulb text-orange-400 shrink-0 mt-0.5 text-sm"></i>
                    <p class="text-sm text-orange-700">Chaque SMS programmé consomme 1 crédit. Configurez l'envoi dans Paramètres › Messages programmés.</p>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <button v-for="pack in smsPacks" :key="pack.sms" type="button"
                      class="text-left bg-white rounded-2xl border-2 p-5 cursor-pointer transition-all relative focus:outline-none flex flex-col gap-3"
                      :class="[smsSelectedPack === pack.sms ? 'border-orange-500 shadow-[0_0_0_3px_rgba(240,90,0,0.12)]' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-md', pack.popular ? 'md:scale-[1.025]' : '']"
                      @click="smsSelectedPack = pack.sms"
                    >
                      <div v-if="pack.popular" class="absolute -top-3 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-[10px] font-mono uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">⭐ Meilleure valeur</div>
                      <div class="flex items-start justify-between">
                        <div><p class="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-0.5">{{ pack.label }}</p><p class="font-bold text-lg text-neutral-900">{{ pack.sms.toLocaleString('fr-FR') }} SMS</p></div>
                        <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center text-white shrink-0 mt-0.5" :class="smsSelectedPack === pack.sms ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'"><i v-if="smsSelectedPack === pack.sms" class="fas fa-check" style="font-size:9px;"></i></div>
                      </div>
                      <div><span class="text-2xl font-bold text-neutral-900">{{ pack.price }} €</span><p class="text-xs text-neutral-400 font-mono mt-0.5">{{ pack.unit }} ct / SMS</p></div>
                      <div v-if="pack.saving" class="inline-flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-100 px-2 py-1 rounded-full font-mono w-fit"><i class="fas fa-tag" style="font-size:9px;"></i> {{ pack.saving }}</div>
                      <div class="border-t border-neutral-100 pt-3">
                        <div class="flex rounded-full overflow-hidden h-1.5 bg-neutral-100 mb-2"><div class="bg-orange-400" style="width:80%"></div><div class="bg-blue-300" style="width:20%"></div></div>
                        <div class="flex items-center justify-between text-xs text-neutral-400"><span>≈ {{ pack.interventions.toLocaleString('fr-FR') }} interventions</span><span>{{ pack.contrats.toLocaleString('fr-FR') }} contrats</span></div>
                      </div>
                    </button>
                  </div>
                  <button class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-7 py-3 rounded-xl shadow-[0_4px_14px_rgba(240,90,0,0.3)] transition-all disabled:opacity-40 disabled:cursor-not-allowed" :disabled="!smsSelectedPack" @click="smsGoToPayment">
                    Continuer <i class="fas fa-arrow-right text-sm"></i>
                  </button>
                </section>

                <section v-if="smsStep === 'payment'">
                  <div class="flex items-center justify-between bg-white border border-neutral-200 rounded-2xl px-5 py-4 mb-5">
                    <button class="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-700 transition-colors" @click="smsStep = 'packs'"><i class="fas fa-chevron-left text-xs"></i> Modifier</button>
                    <div class="text-right"><p class="text-sm font-semibold text-neutral-900">{{ smsCurrentPack.sms.toLocaleString('fr-FR') }} SMS</p><p class="text-xs font-mono text-neutral-400">{{ smsCurrentPack.price }} €</p></div>
                  </div>
                  <div class="space-y-4">
                    <div v-if="paymentMethods.length" class="bg-white border border-neutral-200 rounded-2xl p-6">
                      <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-4">Moyen de paiement</p>
                      <div class="space-y-2">
                        <label v-for="pm in paymentMethods" :key="pm.id" class="flex items-center justify-between border-2 rounded-xl px-4 py-3 cursor-pointer transition-all" :class="smsSelectedPmId === pm.id ? 'border-orange-500 bg-orange-50' : 'border-neutral-200 hover:border-neutral-300'">
                          <input type="radio" :value="pm.id" v-model="smsSelectedPmId" class="absolute opacity-0 w-0 h-0" />
                          <div class="flex items-center gap-3">
                            <i :class="brandIcon(pm.brand)" class="text-2xl text-neutral-400"></i>
                            <div><p class="text-sm font-mono font-medium text-neutral-800">•••• {{ pm.last4 }}</p><p class="text-xs text-neutral-400">Expire {{ pm.exp_month }}/{{ pm.exp_year }}</p></div>
                          </div>
                          <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center" :class="smsSelectedPmId === pm.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'"><div v-if="smsSelectedPmId === pm.id" class="w-1.5 h-1.5 rounded-full bg-white"></div></div>
                        </label>
                      </div>
                      <button class="mt-3 w-full border-2 border-dashed border-neutral-200 hover:border-orange-400 hover:bg-orange-50 text-orange-500 text-sm py-2.5 rounded-xl flex items-center justify-center gap-2 transition-all" @click="smsShowNewCard = !smsShowNewCard">
                        <i :class="smsShowNewCard ? 'fas fa-minus' : 'fas fa-plus'" class="text-xs"></i> {{ smsShowNewCard ? 'Annuler' : 'Autre carte' }}
                      </button>
                    </div>
                    <div v-show="!paymentMethods.length || smsShowNewCard" class="bg-white border border-neutral-200 rounded-2xl p-6">
                      <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5">{{ paymentMethods.length ? 'Nouvelle carte' : 'Carte bancaire' }}</p>
                      <div class="space-y-4">
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Numéro de carte</label><div ref="smsCardNumber" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                        <div class="grid grid-cols-2 gap-3">
                          <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Expiration</label><div ref="smsCardExpiry" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                          <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">CVC</label><div ref="smsCardCvc" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                        </div>
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Titulaire</label><input v-model="smsCardHolder" type="text" class="w-full border border-neutral-200 rounded-xl px-4 py-3 bg-neutral-50 text-sm placeholder-neutral-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all" placeholder="Jean Dupont" /></div>
                      </div>
                    </div>
                    <div v-if="smsErrorMsg" class="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 text-sm text-red-700"><i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-xs"></i><span>{{ smsErrorMsg }}</span></div>
                    <button class="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed" :disabled="smsPaying || !smsCanPay" @click="smsPay">
                      <span v-if="smsPaying"><i class="fas fa-circle-notch fa-spin text-xs"></i>&nbsp; Traitement…</span>
                      <span v-else><i class="fas fa-lock text-xs"></i>&nbsp; Payer {{ smsCurrentPack.price }} €</span>
                    </button>
                    <p class="text-center text-xs text-neutral-400">En confirmant, vous acceptez nos <a href="https://storage.victoranw.fr/file/x3jvJaPzGU" target="_blank" class="text-orange-500 hover:underline">CGU</a> et <a href="https://storage.victoranw.fr/file/ADo4JPI8RA" target="_blank" class="text-orange-500 hover:underline">CGV</a>.</p>
                  </div>
                </section>

                <section v-if="smsStep === 'success'" class="flex items-center justify-center min-h-[360px]">
                  <div class="bg-white border border-neutral-200 rounded-2xl p-10 max-w-sm w-full text-center shadow-xl">
                    <div class="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl mx-auto mb-5 shadow-[0_8px_24px_rgba(240,90,0,0.35)]"><i class="fas fa-check"></i></div>
                    <h2 class="text-xl font-bold text-neutral-900 mb-2">Crédits ajoutés !</h2>
                    <p class="text-sm text-neutral-400 mb-1"><strong class="text-neutral-700">{{ smsCurrentPack.sms.toLocaleString('fr-FR') }} SMS</strong> crédités.</p>
                    <p class="text-sm text-neutral-400 mb-6">Nouveau solde : <strong class="text-neutral-700">{{ smsNewBalance.toLocaleString('fr-FR') }} SMS</strong></p>
                    <button class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-xl transition-all" @click="smsReset">Racheter des crédits <i class="fas fa-arrow-right text-xs"></i></button>
                  </div>
                </section>
              </div>


              <!-- LICENCES -->
              <div v-if="activeTab === 'licences'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Licences</h2>
                  <p class="text-sm text-neutral-400 mt-1">Une licence = un technicien / utilisateur actif</p>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 items-start">
                  <div class="bg-white border border-neutral-200 rounded-2xl p-7 shadow-sm">
                    <div class="flex items-center gap-4 mb-7 pb-7 border-b border-neutral-100">
                      <div class="flex-1">
                        <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-1">Actuellement</p>
                        <p class="text-4xl font-bold text-neutral-900 tabular-nums">{{ societe.nb_licences_max || 1 }} <span class="text-lg font-normal text-neutral-400">licence{{ (societe.nb_licences_max || 1) > 1 ? 's' : '' }}</span></p>
                      </div>
                      <div v-if="(societe.nb_licences_max_periode || 0) > (societe.nb_licences_max || 1)" class="flex items-center gap-1.5 text-xs bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-xl font-mono">
                        <i class="fas fa-level-up-alt text-[10px]"></i> Pic : {{ societe.nb_licences_max_periode }}
                      </div>
                    </div>
                    <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5">Nouveau nombre</p>
                    <div class="my-4">
                      <p v-if="licencesSuccess" class="text-sm bg-green-500 text-white p-4 rounded-xl flex items-center gap-2"><i class="fas fa-check-circle"></i> Vous avez mis à jour votre nombre de licences</p>
                      <p v-if="licencesError" class="text-sm bg-red-500 text-white p-4 rounded-xl flex items-center gap-2"><i class="fas fa-exclamation-triangle"></i> {{ licencesError }}</p>
                    </div>
                    <div class="flex items-center gap-6 py-6 mb-5 bg-[#f9f9f8] rounded-2xl border border-neutral-100">
                      <button class="ml-6 w-14 h-14 rounded-xl border-2 border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-600 flex items-center justify-center text-2xl font-light transition-all disabled:opacity-25 disabled:cursor-not-allowed" :disabled="editLicences <= 1" @click="editLicences--">−</button>
                      <div class="flex-1 text-center">
                        <p class="text-8xl font-bold text-neutral-900 tabular-nums leading-none">{{ editLicences }}</p>
                        <p class="text-xs text-neutral-400 mt-3 font-mono">{{ editLicences > 1 ? 'licences' : 'licence' }}</p>
                      </div>
                      <button class="mr-6 w-14 h-14 rounded-xl border-2 border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-600 flex items-center justify-center text-2xl font-light transition-all" @click="editLicences++">+</button>
                    </div>
                    <div class="flex flex-wrap gap-2 mb-6">
                      <button v-for="q in [1,2,3,4,5,10,15,20,30]" :key="q" class="text-xs font-mono px-3 py-1.5 rounded-lg border transition-all" :class="editLicences === q ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'" @click="editLicences = q">{{ q }}</button>
                    </div>
                    <div v-if="editLicences < (societe.nb_licences_max_periode || societe.nb_licences_max || 1)" class="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700 mb-5">
                      <i class="fas fa-info-circle mt-0.5 shrink-0"></i>
                      <span>Le prochain paiement sera calculé sur <strong>{{ societe.nb_licences_max_periode || societe.nb_licences_max }} licences</strong> (pic période). Réduction effective au cycle suivant.</span>
                    </div>
                    <div class="flex gap-3">
                      <button class="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3.5 rounded-xl transition-all shadow-[0_4px_14px_rgba(240,90,0,0.2)] disabled:opacity-40 disabled:cursor-not-allowed" :disabled="editLicences === (societe.nb_licences_max || 1) || updatingLicences" @click="updateLicences">
                        <span v-if="updatingLicences"><i class="fas fa-circle-notch fa-spin text-xs"></i> Enregistrement…</span>
                        <span v-else><i class="fas fa-save text-xs"></i> Enregistrer</span>
                      </button>
                      <button v-if="editLicences !== (societe.nb_licences_max || 1)" class="px-5 py-3.5 border border-neutral-200 hover:border-neutral-300 text-neutral-500 text-sm rounded-xl transition-all" @click="editLicences = societe.nb_licences_max || 1">Annuler</button>
                    </div>
                  </div>
                  <div class="space-y-4">
                    <div class="bg-neutral-900 text-white rounded-2xl p-5">
                      <p class="text-xs font-mono uppercase tracking-widest text-white/30 mb-4">Aperçu facturation</p>
                      <div class="space-y-0">
                        <div class="flex justify-between text-sm text-white/50 py-2.5 border-b border-white/[0.07]"><span>Licences</span><span class="text-white font-mono">{{ editLicences }}</span></div>
                        <div class="flex justify-between text-sm text-white/50 py-2.5 border-b border-white/[0.07]"><span>Prix unitaire TTC</span><span class="text-white font-mono">{{ fmt(prixEffectif, 'EUR') }}</span></div>
                        <div class="flex justify-between font-bold py-4 text-base"><span>Total / mois</span><span class="text-orange-400 font-mono">{{ fmt(editLicences * prixEffectif, 'EUR') }}</span></div>
                      </div>
                      <div v-if="editLicences !== (societe.nb_licences_max || 1)" class="pt-3 border-t border-white/10">
                        <p class="text-xs text-white/30">
                          <span v-if="editLicences > (societe.nb_licences_max || 1)">+ {{ fmt((editLicences - (societe.nb_licences_max || 1)) * prixEffectif, 'EUR') }} / mois</span>
                          <span v-else>− {{ fmt(((societe.nb_licences_max || 1) - editLicences) * prixEffectif, 'EUR') }} / mois (cycle suivant)</span>
                        </p>
                      </div>
                    </div>
                    <div class="bg-white border border-neutral-200 rounded-2xl p-5">
                      <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-2">Prochain paiement</p>
                      <p class="text-base font-bold text-neutral-900">{{ nextPaymentDate }}</p>
                      <p class="text-sm font-mono text-neutral-400 mt-0.5">{{ fmt(montantProchain, 'EUR') }} TTC</p>
                    </div>
                    <div class="bg-neutral-50 border border-neutral-100 rounded-2xl p-4 text-xs text-neutral-500 leading-relaxed">
                      <i class="fas fa-info-circle text-neutral-400 mr-1.5"></i>La facturation retient le <strong class="text-neutral-700">maximum de licences</strong> atteint dans la période en cours.
                    </div>
                  </div>
                </div>
              </div>


              <!-- FACTURES -->
              <div v-if="activeTab === 'factures'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Factures</h2>
                  <p class="text-sm text-neutral-400 mt-1">{{ factures.length }} facture{{ factures.length !== 1 ? 's' : '' }} · PDF disponibles</p>
                </div>
                <div class="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
                  <div v-if="loadingInvoices" class="flex justify-center py-14"><div class="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div></div>
                  <div v-else-if="factures.length" class="overflow-x-auto">
                    <table class="w-full text-sm">
                      <thead class="bg-neutral-50 border-b border-neutral-100">
                        <tr>
                          <th class="text-left text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-6 py-4">N° Facture</th>
                          <th class="text-left text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-4 py-4">Date</th>
                          <th class="text-left text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-4 py-4 hidden md:table-cell">Description</th>
                          <th class="text-right text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-4 py-4">HT</th>
                          <th class="text-right text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-4 py-4">TTC</th>
                          <th class="text-left text-[10px] font-mono uppercase tracking-wider text-neutral-400 font-normal px-4 py-4">Statut</th>
                          <th class="px-6 py-4"></th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-neutral-50">
                        <tr v-for="f in factures" :key="f.id" class="hover:bg-neutral-50/60 transition-colors">
                          <td class="px-6 py-4 font-mono text-xs font-semibold text-neutral-700">{{ f.numero || ('FAC-' + f.id) }}</td>
                          <td class="px-4 py-4 text-xs text-neutral-400 whitespace-nowrap">{{ f.created_at }}</td>
                          <td class="px-4 py-4 text-xs text-neutral-400 max-w-[180px] truncate hidden md:table-cell">{{ f.description }}</td>
                          <td class="px-4 py-4 text-right font-mono text-sm text-neutral-600 whitespace-nowrap">{{ fmt(f.montant_ht, f.devise) }}</td>
                          <td class="px-4 py-4 text-right font-mono text-sm font-bold text-neutral-900 whitespace-nowrap">{{ fmt(f.montant_ttc, f.devise) }}</td>
                          <td class="px-4 py-4">
                            <span class="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide px-2.5 py-1 rounded-full" :class="badgeClass(f.statut)">
                              <i :class="badgeIcon(f.statut)" class="text-[8px]"></i>{{ badgeLabel(f.statut) }}
                            </span>
                          </td>
                          <td class="px-6 py-4">
                            <a v-if="f.vosfactures_url" :href="f.vosfactures_url" target="_blank" class="w-8 h-8 rounded-lg bg-red-50 hover:bg-red-100 text-red-400 flex items-center justify-center transition-colors"><i class="fas fa-file-pdf text-xs"></i></a>
                            <span v-else class="text-neutral-300 text-xs font-mono">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else class="text-center py-16 text-neutral-400">
                    <i class="fas fa-file-invoice text-4xl opacity-20 mb-3 block"></i>
                    <p class="text-sm">Aucune facture pour le moment.</p>
                    <p class="text-xs opacity-60 mt-1">Elle apparaîtra ici après votre premier paiement.</p>
                  </div>
                </div>
              </div>


              <!-- MOYENS DE PAIEMENT -->
              <div v-if="activeTab === 'paiement'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Moyens de paiement</h2>
                  <p class="text-sm text-neutral-400 mt-1">Cartes enregistrées sur votre compte</p>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start">
                  <div class="bg-white border border-neutral-200 rounded-2xl shadow-sm overflow-hidden">
                    <div v-if="loadingCards" class="flex justify-center py-10"><div class="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div></div>
                    <div v-else-if="paymentMethods.length" class="divide-y divide-neutral-50">
                      <div v-for="pm in paymentMethods" :key="pm.id" class="flex items-center justify-between px-6 py-4 hover:bg-neutral-50 transition-colors" :class="pm.is_default ? 'bg-orange-50/40' : ''">
                        <div class="flex items-center gap-4">
                          <i :class="brandIcon(pm.brand)" class="text-3xl text-neutral-400"></i>
                          <div>
                            <div class="flex items-center gap-2">
                              <p class="text-sm font-mono font-semibold text-neutral-800">{{ pm.brand }} •••• {{ pm.last4 }}</p>
                              <span v-if="pm.is_default" class="text-[10px] bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full font-mono uppercase tracking-wide">défaut</span>
                            </div>
                            <p class="text-xs text-neutral-400 mt-0.5">Expire {{ pm.exp_month }}/{{ pm.exp_year }}</p>
                          </div>
                        </div>
                        <div class="flex items-center gap-2">
                          <button v-if="!pm.is_default" class="text-xs border border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-400 px-3 py-1.5 rounded-lg transition-all disabled:opacity-50" :disabled="settingDefault" @click="setDefaultCard(pm.id)">Définir par défaut</button>
                          <button class="w-8 h-8 rounded-lg border border-neutral-200 hover:border-red-300 hover:bg-red-50 text-neutral-300 hover:text-red-400 flex items-center justify-center transition-all" @click="deleteCard(pm.id)"><i class="fas fa-trash-alt text-xs"></i></button>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center py-14 text-neutral-400"><i class="fas fa-credit-card text-3xl opacity-20 mb-3 block"></i><p class="text-sm">Aucune carte enregistrée.</p></div>
                  </div>
                  <div class="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                    <h3 class="font-bold text-neutral-900 text-sm mb-5">Ajouter une carte</h3>
                    <div class="space-y-3.5">
                      <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Numéro de carte</label><div ref="changeCardNumber" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                      <div class="grid grid-cols-2 gap-3">
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Expiration</label><div ref="changeCardExpiry" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                        <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">CVC</label><div ref="changeCardCvc" class="border border-neutral-200 rounded-xl px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div></div>
                      </div>
                      <div><label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Titulaire</label><input v-model="changeCardHolder" type="text" class="w-full border border-neutral-200 rounded-xl px-4 py-2.5 bg-neutral-50 text-sm placeholder-neutral-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all" placeholder="Jean Dupont" /></div>
                    </div>
                    <div class="mt-3 flex items-center gap-2 text-xs text-neutral-400 font-mono px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-100">
                      <i class="fas fa-lock text-green-500"></i> SSL <span class="text-neutral-300 mx-1">·</span> <i class="fab fa-stripe" style="color:#635bff;"></i> Stripe <span class="text-neutral-300 mx-1">·</span> <i class="fas fa-id-card text-blue-400"></i> 3D Secure
                    </div>
                    <div v-if="changeCardError" class="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-sm text-red-700"><i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-xs"></i> {{ changeCardError }}</div>
                    <div v-if="changeCardSuccess" class="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-2.5 text-sm text-green-700"><i class="fas fa-check-circle text-xs"></i> Carte enregistrée.</div>
                    <button class="mt-4 w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold py-3 rounded-xl transition-all disabled:opacity-50" :disabled="addingCard" @click="submitNewDefaultCard">
                      <span v-if="addingCard"><i class="fas fa-circle-notch fa-spin text-xs"></i> Enregistrement…</span>
                      <span v-else><i class="fas fa-lock text-xs"></i> Enregistrer la carte</span>
                    </button>
                  </div>
                </div>
              </div>


              <!-- INFOS ABONNEMENT -->
              <div v-if="activeTab === 'infos'">
                <div class="mb-7">
                  <h2 class="text-2xl font-bold text-neutral-900">Infos abonnement</h2>
                  <p class="text-sm text-neutral-400 mt-1">Statut, facturation et résiliation</p>
                </div>
                <div class="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5">
                  <div class="space-y-4">
                    <div class="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                      <div class="flex items-center justify-between mb-5">
                        <div class="flex items-center gap-3">
                          <div class="relative w-2.5 h-2.5 shrink-0"><div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-60"></div><div class="w-2.5 h-2.5 rounded-full bg-green-400 relative"></div></div>
                          <span class="font-bold text-green-600">Abonnement actif</span>
                        </div>
                        <span class="text-xs font-mono bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full">En cours</span>
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <div class="bg-neutral-50 rounded-xl p-4"><p class="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Prix / licence</p><p class="font-bold text-neutral-900 font-mono">{{ fmt(prixEffectif, 'EUR') }} TTC</p></div>
                        <div class="bg-neutral-50 rounded-xl p-4"><p class="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Licences actives</p><p class="font-bold text-neutral-900 font-mono">{{ societe.nb_licences_max || 1 }}</p></div>
                        <div class="bg-neutral-50 rounded-xl p-4"><p class="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Depuis le</p><p class="font-bold text-neutral-900 text-sm">{{ dateDebutAbonnement }}</p></div>
                        <div class="bg-neutral-50 rounded-xl p-4"><p class="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Renouvellement</p><p class="font-bold text-neutral-900 text-sm">{{ nextPaymentDate }}</p></div>
                      </div>
                    </div>
                    <div class="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                      <h3 class="font-semibold text-neutral-900 mb-4">Prochain paiement</h3>
                      <div class="space-y-3">
                        <div class="flex justify-between text-sm"><span class="text-neutral-500">Licences facturées</span><span class="font-medium text-neutral-800 font-mono">{{ societe.nb_licences_max_periode || societe.nb_licences_max || 1 }}</span></div>
                        <div class="flex justify-between text-sm"><span class="text-neutral-500">Prix unitaire TTC</span><span class="font-medium text-neutral-800 font-mono">{{ fmt(prixEffectif, 'EUR') }}</span></div>
                        <div class="border-t border-neutral-100 pt-3 flex justify-between items-center"><span class="font-bold text-neutral-900">Montant TTC</span><span class="text-xl font-bold text-neutral-900 font-mono">{{ fmt(montantProchain, 'EUR') }}</span></div>
                      </div>
                    </div>
                    <div class="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm">
                      <h3 class="font-semibold text-neutral-900 mb-1.5">Résilier l'abonnement</h3>
                      <p class="text-xs text-neutral-400 leading-relaxed mb-4">Envoyez un email avec votre SIRET pour résilier sans frais ni préavis.</p>
                      <button class="flex items-center gap-2 text-sm border border-red-200 hover:bg-red-50 text-red-400 hover:text-red-600 px-4 py-2.5 rounded-xl transition-all" @click="confirmResil = true">
                        <i class="fas fa-times-circle text-xs"></i> Demander la résiliation
                      </button>
                    </div>
                  </div>
                  <div class="space-y-3">
                    <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-1">Accès rapide</p>
                    <button v-for="item in navItems.filter(i => i.id !== 'infos')" :key="item.id" class="w-full flex items-center gap-3 bg-white border border-neutral-200 rounded-xl px-4 py-3.5 hover:border-orange-200 hover:shadow-sm transition-all text-left group" @click="activeTab = item.id">
                      <div class="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0 group-hover:bg-orange-100 transition-colors"><i :class="item.icon" class="text-orange-500 text-xs"></i></div>
                      <span class="text-sm font-medium text-neutral-700">{{ item.label }}</span>
                      <i class="fas fa-chevron-right text-neutral-300 text-xs ml-auto"></i>
                    </button>
                  </div>
                </div>
              </div>

            </template>
          </div>
        </main>
      </div>


      <!-- MODAL Résiliation -->
      <div v-if="confirmResil" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" @click.self="confirmResil = false">
        <div class="bg-white rounded-2xl p-7 w-full max-w-sm shadow-2xl">
          <div class="flex items-center justify-between mb-5">
            <h3 class="font-bold text-lg text-neutral-900">Résilier l'abonnement</h3>
            <button class="text-neutral-400 hover:text-neutral-700 w-7 h-7 flex items-center justify-center" @click="confirmResil = false"><i class="fas fa-times text-sm"></i></button>
          </div>
          <p class="text-sm text-neutral-500 leading-relaxed mb-3">Envoyez-nous un email avec votre numéro SIRET :</p>
          <div class="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 font-mono text-sm text-neutral-800 mb-5 tracking-widest">{{ societe.siret }}</div>
          <a :href="`mailto:contact@termoflow.fr?subject=Résiliation abonnement — ${societe.siret}`" class="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-3 rounded-xl transition-all text-sm">
            <i class="fas fa-envelope text-xs"></i> Contacter le support
          </a>
        </div>
      </div>

      <modal name="modal-confirm-licences" height="auto" :scrollable="true" :adaptive="true">
        <div class="p-10 overflow-y-auto max-h-[100vh]">
          <div class="flex items-center justify-between mb-4 pb-4 border-b text-2xl">
            <h2 class="font-bold">Augmentation de licences</h2>
            <button @click="$modal.hide('modal-confirm-licences')">
              <i class="fas fa-times text-red-500"></i>
            </button>
          </div>
          <div class="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 text-sm text-amber-800 mb-6">
            <i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-amber-500"></i>
            <div>
              <p class="font-semibold mb-1">Facturation immédiatement impactée</p>
              <p class="text-amber-700 leading-relaxed">
                En passant à <strong>{{ pendingLicenceCount }} licence{{ pendingLicenceCount > 1 ? 's' : '' }}</strong>, 
                votre prochain paiement sera de <strong>{{ fmt(pendingLicenceCount * prixEffectif, 'EUR') }} TTC</strong> — 
                même si vous réduisez vos licences avant la date de renouvellement.
              </p>
            </div>
          </div>
          <label class="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" v-model="licenceConfirmChecked" class="mt-1 accent-orange-500 w-4 h-4 shrink-0" />
            <span class="text-sm text-neutral-700 leading-relaxed">
              J'accepte que ma prochaine facture s'élève à 
              <strong class="text-orange-600">{{ fmt(pendingLicenceCount * prixEffectif, 'EUR') }} TTC</strong> 
              ({{ pendingLicenceCount }} licence{{ pendingLicenceCount > 1 ? 's' : '' }} × {{ fmt(prixEffectif, 'EUR') }})
            </span>
          </label>
          <div class="flex justify-end mt-8">
            <button
              :disabled="!licenceConfirmChecked || updatingLicences"
              @click="confirmLicences"
              class="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-7 py-3 rounded-xl transition-all shadow-[0_4px_14px_rgba(240,90,0,0.3)]"
            >
              <span v-if="updatingLicences"><i class="fas fa-circle-notch fa-spin text-xs"></i> Enregistrement…</span>
              <span v-else><i class="fas fa-check text-xs"></i> Confirmer — {{ fmt(pendingLicenceCount * prixEffectif, 'EUR') }} / mois</span>
            </button>
          </div>
        </div>
      </modal>

    </app-template>
  </div>
</template>

<script>
/* global Stripe */
import env from '@/services/env.service'
import moment from 'moment'

const STANDARD_PRICES = [30, 55]
const STRIPE_STYLE = {
  base: { fontSize: '15px', color: '#1a1a1a', fontSmoothing: 'antialiased', '::placeholder': { color: '#c4c4c4' } },
  invalid: { color: '#c0392b', iconColor: '#c0392b' },
}
function usageSplit (total) {
  const interventions = Math.round(total * 0.8)
  return { interventions, contrats: total - interventions }
}

export default {
  name: 'ParametreAbonnement',

  data () {
    return {
      loading: true,
      step: 'configure',
      activeTab: 'sms',

      navItems: [
        { id: 'sms',      label: 'Crédits SMS',       icon: 'fas fa-comment-dots' },
        { id: 'licences', label: 'Licences',           icon: 'fas fa-users' },
        { id: 'factures', label: 'Factures',           icon: 'fas fa-file-invoice' },
        { id: 'paiement', label: 'Moyens de paiement', icon: 'fas fa-credit-card' },
        { id: 'infos',    label: 'Infos abonnement',   icon: 'fas fa-layer-group' },
      ],

      plans: [
        {
          id: 'independant', name: 'Indépendant', tagline: "L'arsenal complet pour l'artisan qui veut une structure de grand groupe.",
          icon: 'fas fa-user', recommended: false, prix: 30, stripe_product_id: 'prod_UJyeb8na3jba1h',
          features: [
            { label: 'Interventions & planning', included: true }, { label: 'App mobile métier (offline)', included: true },
            { label: 'CERFA & conformité légale', included: true }, { label: 'Stock & traçabilité fluides', included: true },
            { label: 'Relation client & historique', included: true }, { label: 'Stats, exports & facturation', included: true },
            { label: 'Portail client premium', included: false }, { label: 'Reporting stratégique', included: false },
          ],
        },
        {
          id: 'entreprise', name: 'Entreprise', tagline: "Pour piloter vos équipes, vos stocks et votre image avec précision.",
          icon: 'fas fa-building', recommended: true, prix: 55, stripe_product_id: 'prod_UJyeb8na3jba1h',
          features: [
            { label: "Tout l'Indépendant", included: true }, { label: 'Pilotage multi-techniciens', included: true },
            { label: 'Logistique multi-stocks', included: true }, { label: 'Portail client premium', included: true },
            { label: 'Marketing automatisé', included: true }, { label: 'Reporting stratégique', included: true },
            { label: 'Audit & traçabilité renforcée', included: true }, { label: 'Support prioritaire', included: true },
          ],
        },
      ],
      selectedPlanId: null,

      nbLicences: 1, editLicences: 1,
      updatingLicences: false, licencesSuccess: false, licencesError: null,

      stripe: null, elements: null,
      elNumber: null, elExpiry: null, elCvc: null, cardHolder: '', stripeReady: false,
      changeElNumber: null, changeElExpiry: null, changeElCvc: null,
      changeCardHolder: '', changeCardReady: false, changeCardError: null, changeCardSuccess: false,

      paymentMethods: [], selectedPmId: null, showNewCard: false, loadingCards: false,
      factures: [], loadingInvoices: false,
      paying: false, errorMsg: null, addingCard: false, settingDefault: false, confirmResil: false,

      smsStep: 'packs', smsNewBalance: 0,
      smsPacks: [
        { sms: 500,  label: 'Starter',  price: '39',  price_amount: 3900,  unit: '7,8',  popular: false, saving: null,               ...usageSplit(500) },
        { sms: 2000, label: 'Business', price: '133', price_amount: 13300, unit: '6,65', popular: true,  saving: '− 15 % vs Starter', ...usageSplit(2000) },
        { sms: 5000, label: 'Scale',    price: '292', price_amount: 29200, unit: '5,84', popular: false, saving: '− 25 % vs Starter', ...usageSplit(5000) },
      ],
      smsSelectedPack: null,
      smsElNumber: null, smsElExpiry: null, smsElCvc: null,
      smsCardHolder: '', smsStripeReady: false,
      smsSelectedPmId: null, smsShowNewCard: false, smsPaying: false, smsErrorMsg: null,

      licenceConfirmChecked: false,
      pendingLicenceCount: null,
    }
  },

  filters: {
      momentDate(value) {
          if (!value) {
              return '-';
          }
          return moment(value).format('DD/MM/YYYY');
      }
  },

  computed: {
    societe ()          { return this.$account?.account?.societe || {} },
    isSubscribed ()     { return !!this.societe.stripe_product_id },
    isNegotiatedRate () {
      const p = parseFloat(this.societe.prix_par_licence || 0)
      return p > 0 && !STANDARD_PRICES.includes(p)
    },
    prixEffectif () {
      if (this.isNegotiatedRate) return parseFloat(this.societe.prix_par_licence)
      if (this.isSubscribed)     return parseFloat(this.societe.prix_par_licence || 0)
      return this.currentPlan?.prix || 0
    },
    currentPlan ()     { return this.plans.find(p => p.id === this.selectedPlanId) || null },
    totalTTC ()        { return this.nbLicences * this.prixEffectif },
    stripeAmount ()    { return Math.round(this.totalTTC * 100) },
    canPay ()          { return (this.paymentMethods.length && !this.showNewCard && this.selectedPmId) || this.stripeReady },
    montantProchain () { return (this.societe.nb_licences_max_periode || this.societe.nb_licences_max || 1) * this.prixEffectif },
    nextPaymentDate () {
      if (this.societe.date_prochain_paiement) return this.formatDate(this.societe.date_prochain_paiement)
      const d = new Date(); d.setMonth(d.getMonth() + 1)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    dateDebutAbonnement () { return this.formatDate(this.societe.date_debut_abonnement) },
    smsCurrentPack ()  { return this.smsPacks.find(p => p.sms === this.smsSelectedPack) || this.smsPacks[0] },
    smsCanPay ()       { return (this.paymentMethods.length && !this.smsShowNewCard && this.smsSelectedPmId) || this.smsStripeReady },
  },

  async mounted () { await this.init() },
  beforeDestroy ()  { this.destroyAll() },

  watch: {
    activeTab (tab) {
      if (tab === 'paiement') this.$nextTick(async () => { await this.loadStripe(); this.mountChangeCardElements() })
      if (tab === 'sms' && !this.paymentMethods.length) this.loadCards()
    },
  },

  methods: {
    async init () {
      this.loading = true
      try {
        if (!this.societe?.id) return
        if (this.isSubscribed) {
          this.editLicences = this.societe.nb_licences_max || 1
          await Promise.all([this.loadCards(), this.loadInvoices()])
          return
        }
        if (this.societe.stripe_id) await this.loadCards()
        const prix = parseFloat(this.societe.prix_par_licence || 0)
        if (prix === 55) this.selectedPlanId = 'entreprise'
        else if (prix === 30) this.selectedPlanId = 'independant'
        this.step = 'configure'
      } catch (e) { console.error('init', e) }
      finally { this.loading = false }
    },

    async goToPayment () {
      this.step = 'payment'; this.errorMsg = null; this.stripeReady = false
      await this.$nextTick(); await this.$nextTick()
      await this.loadStripe(); this.mountPaymentElements()
    },

    async handleSuccess () { window.location.reload() },
    switchTab (tabId)      { this.activeTab = tabId },

    async loadStripe () {
      if (this.stripe) return
      const pk = env.stripeKey
      if (!pk) { this.errorMsg = 'Configuration Stripe manquante.'; throw new Error('PK') }
      if (!window.Stripe) {
        await new Promise((res, rej) => {
          const s = document.createElement('script'); s.src = 'https://js.stripe.com/v3/'
          s.onload = res; s.onerror = () => rej(new Error('Stripe.js')); document.head.appendChild(s)
        })
      }
      this.stripe = Stripe(pk); this.elements = this.stripe.elements()
    },

    mountElements (rNum, rExp, rCvc, onReady, onChange) {
      const nEl = this.$refs[rNum], eEl = this.$refs[rExp], cEl = this.$refs[rCvc]
      if (!nEl || !eEl || !cEl) { setTimeout(() => this.mountElements(rNum, rExp, rCvc, onReady, onChange), 100); return }
      const elNum = this.elements.create('cardNumber', { style: STRIPE_STYLE, showIcon: true })
      const elExp = this.elements.create('cardExpiry', { style: STRIPE_STYLE })
      const elCvc = this.elements.create('cardCvc',    { style: STRIPE_STYLE })
      nEl.innerHTML = ''; eEl.innerHTML = ''; cEl.innerHTML = ''
      elNum.mount(nEl); elExp.mount(eEl); elCvc.mount(cEl)
      elNum.on('ready', onReady)
      elNum.on('change', e => { if (onChange) onChange(e.error?.message || null) })
      return { elNum, elExp, elCvc }
    },

    mountPaymentElements () {
      ;[this.elNumber, this.elExpiry, this.elCvc].forEach(el => { if (el) { try { el.destroy() } catch (_) {} } })
      const els = this.mountElements('cardNumber', 'cardExpiry', 'cardCvc', () => { this.stripeReady = true }, msg => { this.errorMsg = msg })
      if (els) { this.elNumber = els.elNum; this.elExpiry = els.elExp; this.elCvc = els.elCvc }
    },
    mountChangeCardElements () {
      ;[this.changeElNumber, this.changeElExpiry, this.changeElCvc].forEach(el => { if (el) { try { el.destroy() } catch (_) {} } })
      const els = this.mountElements('changeCardNumber', 'changeCardExpiry', 'changeCardCvc', () => { this.changeCardReady = true }, msg => { this.changeCardError = msg })
      if (els) { this.changeElNumber = els.elNum; this.changeElExpiry = els.elExp; this.changeElCvc = els.elCvc }
    },
    mountSmsCardElements () {
      ;[this.smsElNumber, this.smsElExpiry, this.smsElCvc].forEach(el => { if (el) { try { el.destroy() } catch (_) {} } })
      const els = this.mountElements('smsCardNumber', 'smsCardExpiry', 'smsCardCvc', () => { this.smsStripeReady = true }, msg => { this.smsErrorMsg = msg })
      if (els) { this.smsElNumber = els.elNum; this.smsElExpiry = els.elExp; this.smsElCvc = els.elCvc }
    },
    destroyAll () {
      [this.elNumber, this.elExpiry, this.elCvc, this.changeElNumber, this.changeElExpiry, this.changeElCvc, this.smsElNumber, this.smsElExpiry, this.smsElCvc]
        .forEach(el => { if (el) { try { el.destroy() } catch (_) {} } })
    },

    async loadCards () {
      if (!this.societe?.id) return
      this.loadingCards = true
      try {
        const res = await this.$request.get(`/stripe/payment-methods/${this.societe.id}`)
        this.paymentMethods = res.payment_methods || []
        const defaultId = (this.paymentMethods.find(p => p.is_default) || this.paymentMethods[0])?.id || null
        this.selectedPmId = defaultId; this.smsSelectedPmId = defaultId
      } catch (e) { console.error('loadCards', e) }
      finally { this.loadingCards = false }
    },
    async loadInvoices () {
      if (!this.societe?.id) return
      this.loadingInvoices = true
      try { const res = await this.$request.get(`/stripe/factures/${this.societe.id}`); this.factures = res.factures || [] }
      catch (e) { console.error('loadInvoices', e) }
      finally { this.loadingInvoices = false }
    },

    async ensureCustomer (errorTarget = 'subscription') {
      if (this.societe.stripe_id) return true
      try { await this.$request.post('/stripe/customers', { societe_id: this.societe.id }); return true }
      catch {
        const msg = 'Impossible de créer votre profil de paiement.'
        if (errorTarget === 'sms') this.smsErrorMsg = msg; else this.errorMsg = msg
        return false
      }
    },

    async pay () {
      this.errorMsg = null; this.paying = true
      try {
        if (!await this.ensureCustomer()) return
        let pmId = this.selectedPmId
        if (!pmId || this.showNewCard) { pmId = await this.saveCard(this.elNumber, this.cardHolder); if (!pmId) return }
        const planName = this.isNegotiatedRate ? 'Personnalisé' : this.currentPlan?.name
        await this.$request.post('/stripe/charge', {
          societe_id: this.societe.id, amount: this.stripeAmount, currency: 'eur',
          description: `Abonnement ${planName} — ${this.nbLicences} licence${this.nbLicences > 1 ? 's' : ''}`,
          payment_method_id: pmId, taux_tva: 20, nb_licences: this.nbLicences,
          stripe_product_id: this.isNegotiatedRate ? null : this.currentPlan?.stripe_product_id,
        })
        this.step = 'success'
      } catch (e) { this.errorMsg = e?.response?.data?.message || 'Une erreur est survenue.' }
      finally { this.paying = false }
    },

    async saveCard (cardElement, holderName, isChange = false, errorTarget = 'subscription') {
      try {
        const siRes = await this.$request.post('/stripe/setup-intent', { societe_id: this.societe.id })
        const { setupIntent, error } = await this.stripe.confirmCardSetup(siRes.client_secret, {
          payment_method: { card: cardElement, billing_details: { name: holderName || this.societe.raison_sociale || '' } },
        })
        if (error) {
          if (errorTarget === 'sms') this.smsErrorMsg = error.message
          else if (isChange) this.changeCardError = error.message
          else this.errorMsg = error.message
          return null
        }
        await this.$request.post('/stripe/payment-methods/attach', { societe_id: this.societe.id, payment_method_id: setupIntent.payment_method, set_as_default: true })
        return setupIntent.payment_method
      } catch (e) {
        const msg = e?.response?.data?.message || "Erreur lors de l'enregistrement."
        if (errorTarget === 'sms') this.smsErrorMsg = msg
        else if (isChange) this.changeCardError = msg
        else this.errorMsg = msg
        return null
      }
    },

    async updateLicences() {
      if (this.editLicences > (this.societe.nb_licences_max || 1)) {
        this.pendingLicenceCount = this.editLicences
        this.licenceConfirmChecked = false
        this.$modal.show('modal-confirm-licences')
        return
      }
      if (this.editLicences < (this.societe.nb_licences_max || 1)) {
        this.updatingLicences = true
        try {
          const res = await this.$request.get(`/societe/nb-current-licences`)
          if (res > this.editLicences) {
            this.licencesError = `Vous avez actuellement ${res} compte${res > 1 ? 's' : ''} actif${res > 1 ? 's' : ''}. Veuillez en désactiver avant de passer à ${this.editLicences} licence${this.editLicences > 1 ? 's' : ''}.`
            this.editLicences = this.societe.nb_licences_max || 1
            return
          }
        } catch (e) {
          this.licencesError = e?.response?.data?.message || 'Impossible de vérifier le nombre de comptes actifs.'
          return
        } finally {
          this.updatingLicences = false
        }
      }
      await this.doUpdateLicences()
    },

    async confirmLicences() {
      this.$modal.hide('modal-confirm-licences')
      await this.doUpdateLicences()
    },

    async doUpdateLicences() {
      this.updatingLicences = true; this.licencesSuccess = false; this.licencesError = null
      try {
        await this.$request.post('/stripe/licences/update', { societe_id: this.societe.id, nb_licences: this.editLicences })
        this.$account.account.societe.nb_licences_max = this.editLicences
        if (this.editLicences > (this.societe.nb_licences_max_periode || 0)) this.$account.account.societe.nb_licences_max_periode = this.editLicences
        this.licencesSuccess = true; setTimeout(() => { this.licencesSuccess = false }, 4000)
      } catch (e) { this.licencesError = e?.response?.data?.message || 'Erreur lors de la mise à jour.' }
      finally { this.updatingLicences = false }
    },

    async setDefaultCard (pmId) {
      this.settingDefault = true
      try { await this.$request.post('/stripe/payment-methods/set-default', { societe_id: this.societe.id, payment_method_id: pmId }); await this.loadCards() }
      catch (e) { alert(e?.response?.data?.message || 'Erreur.') }
      finally { this.settingDefault = false }
    },
    async submitNewDefaultCard () {
      this.changeCardError = null; this.changeCardSuccess = false; this.addingCard = true
      try {
        if (!await this.ensureCustomer()) return
        const pmId = await this.saveCard(this.changeElNumber, this.changeCardHolder, true)
        if (pmId) { await this.loadCards(); this.changeCardHolder = ''; this.changeCardSuccess = true; setTimeout(() => { this.changeCardSuccess = false }, 4000) }
      } finally { this.addingCard = false }
    },
    async deleteCard (pmId) {
      if (!window.confirm('Supprimer cette carte ?')) return
      try { await this.$request.delete(`/stripe/payment-methods/${pmId}`, { data: { societe_id: this.societe.id } }); await this.loadCards() }
      catch { alert('Impossible de supprimer cette carte.') }
    },

    async smsGoToPayment () {
      this.smsStep = 'payment'; this.smsErrorMsg = null; this.smsStripeReady = false
      if (!this.paymentMethods.length) await this.loadCards()
      this.smsSelectedPmId = this.selectedPmId
      await this.$nextTick(); await this.$nextTick()
      await this.loadStripe(); this.mountSmsCardElements()
    },
    smsReset () { this.smsStep = 'packs'; this.smsSelectedPack = null; this.smsShowNewCard = false; this.smsErrorMsg = null },
    async smsPay () {
      this.smsErrorMsg = null; this.smsPaying = true
      try {
        if (!await this.ensureCustomer('sms')) return
        let pmId = this.smsSelectedPmId
        if (!pmId || this.smsShowNewCard) { pmId = await this.saveCard(this.smsElNumber, this.smsCardHolder, false, 'sms'); if (!pmId) return }
        const res = await this.$request.post('/stripe/sms/buy-credits', {
          societe_id: this.societe.id, sms_pack: this.smsCurrentPack.sms,
          amount: this.smsCurrentPack.price_amount, currency: 'eur',
          description: `Pack ${this.smsCurrentPack.label} — ${this.smsCurrentPack.sms} SMS`,
          payment_method_id: pmId, taux_tva: 0,
        })
        if (this.$account?.account?.societe) this.$account.account.societe.sms_credits = res.new_balance
        this.smsNewBalance = res.new_balance; this.smsStep = 'success'
      } catch (e) { this.smsErrorMsg = e?.response?.data?.message || 'Une erreur est survenue.' }
      finally { this.smsPaying = false }
    },

    brandIcon (brand) {
      const b = (brand || '').toLowerCase()
      if (b === 'visa') return 'fab fa-cc-visa'; if (b === 'mastercard') return 'fab fa-cc-mastercard'
      if (b === 'amex') return 'fab fa-cc-amex';  if (b === 'discover') return 'fab fa-cc-discover'
      return 'fas fa-credit-card'
    },
    fmt (n, devise) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(n) },
    formatDate (dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    badgeLabel (s) { return { payee: 'Payée', en_attente: 'En attente', echouee: 'Échouée', remboursee: 'Remboursée' }[s] || s },
    badgeClass (s) { return { payee: 'bg-green-50 text-green-700', en_attente: 'bg-amber-50 text-amber-700', echouee: 'bg-red-50 text-red-700', remboursee: 'bg-purple-50 text-purple-700' }[s] || 'bg-neutral-100 text-neutral-500' },
    badgeIcon (s)  { return { payee: 'fas fa-check-circle', en_attente: 'fas fa-clock', echouee: 'fas fa-times-circle', remboursee: 'fas fa-undo-alt' }[s] || 'fas fa-circle' },
  },
}
</script>

<style scoped>
[data-elements-stable-field-name="cardNumber"] iframe,
[data-elements-stable-field-name="cardExpiry"] iframe,
[data-elements-stable-field-name="cardCvc"] iframe { display: block !important; width: 100% !important; }
input[type="range"] { -webkit-appearance: none; appearance: none; }
input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; width: 18px; height: 18px; border-radius: 50%; background: #f05a00; cursor: pointer; box-shadow: 0 1px 4px rgba(240,90,0,0.4); }
input[type="range"]::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: #f05a00; cursor: pointer; border: none; }
</style>